import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../services/api';
import DatePicker from '../../components/DatePicker';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';

const bugunStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const ayBasiStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const isoToDp = (iso) => { if (!iso) return ''; const [y, m, g] = iso.split('-'); return `${g}.${m}.${y}`; };
const dpToIso = (dp) => { if (!dp) return ''; const [g, m, y] = dp.split('.'); return `${y}-${m}-${g}`; };

const fmt = (val) =>
  '₺' + (val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const HOTEL_COLORS = [
  ['from-blue-500', 'to-indigo-600'],
  ['from-violet-500', 'to-purple-600'],
  ['from-emerald-500', 'to-teal-600'],
  ['from-orange-500', 'to-red-500'],
  ['from-pink-500', 'to-rose-600'],
  ['from-cyan-500', 'to-blue-500'],
];

// ── Üst özet kartları ──────────────────────────────────────────────────────
function OzetKartlar({ data }) {
  const toplamGelir = data.reduce((s, d) => s + d.toplamGelir, 0);
  const toplamGider = data.reduce((s, d) => s + d.toplamGider, 0);
  const netKar = toplamGelir - toplamGider;
  const karMarji = toplamGelir > 0 ? ((netKar / toplamGelir) * 100).toFixed(1) : 0;
  const toplamOda = data.reduce((s, d) => s + (d.toplamOdaSayisi || 0), 0);
  const doluOda   = data.reduce((s, d) => s + (d.doluOdaSayisi || 0), 0);
  const ortDoluluk = toplamOda > 0 ? Math.round(doluOda / toplamOda * 100) : 0;

  const cards = [
    { label: 'TOPLAM GELİR', val: toplamGelir, sub: `${data.length} otelden`, color: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-200', icon: '💰' },
    { label: 'TOPLAM GİDER', val: toplamGider, sub: `${data.reduce((s, d) => s + d.kasaGiderSayisi, 0)} işlem`, color: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-200', icon: '📉' },
    { label: 'NET KÂR', val: netKar, sub: `%${karMarji} marj`, color: netKar >= 0 ? 'from-indigo-600 to-violet-700' : 'from-gray-600 to-gray-800', shadow: netKar >= 0 ? 'shadow-indigo-200' : 'shadow-gray-200', icon: '💎' },
    { label: 'AKTİF OTEL SAYISI', val: data.length, sub: 'toplam tesis', color: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-200', icon: '🏨', isRaw: true },
    { label: 'TOPLAM DOLULUK', val: ortDoluluk, sub: `${doluOda} / ${toplamOda} oda`, color: 'from-violet-500 to-purple-700', shadow: 'shadow-violet-200', icon: '📈', isRaw: true, suffix: '%' }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
      {cards.map((c, i) => (
        <div key={i} className={`bg-gradient-to-br ${c.color} rounded-[2rem] p-7 text-white shadow-2xl ${c.shadow} relative overflow-hidden group hover:scale-[1.02] transition-[transform,box-shadow] duration-500`}>
          <div className="absolute top-0 right-0 p-6 text-4xl opacity-20 group-hover:scale-125 transition-transform">{c.icon}</div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80 mb-2 leading-none">{c.label}</p>
          <p className="text-2xl font-black tracking-tighter">{c.isRaw ? `${c.val}${c.suffix || ''}` : fmt(c.val)}</p>
          <div className="mt-4 flex items-center gap-2">
             <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
             <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">{c.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Otel detay kartı ───────────────────────────────────────────────────────
function OtelKart({ d, colorIdx }) {
  const [from, to] = HOTEL_COLORS[colorIdx % HOTEL_COLORS.length];
  const karMarji = d.toplamGelir > 0 ? ((d.netKar / d.toplamGelir) * 100).toFixed(1) : 0;
  const giderOrani = d.toplamGelir > 0 ? Math.min((d.toplamGelir > 0 ? d.toplamGider / d.toplamGelir : 0) * 100, 100) : 0;
  const kasaOrani = d.toplamGelir > 0 ? (d.kasaGelirToplam / d.toplamGelir) * 100 : 0;
  const rezOrani = d.toplamGelir > 0 ? (d.rezervasyonOdemeToplam / d.toplamGelir) * 100 : 0;
  const doluluk = d.dolulukYuzde ?? 0;

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden group hover:shadow-2xl hover:shadow-indigo-100/30 transition-[border-color,box-shadow,transform] duration-500 border-2 hover:border-indigo-100">
      <div className={`bg-gradient-to-r ${from} ${to} px-8 py-6 flex items-center justify-between`}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-inner border border-white/30">
            {d.otelAd?.charAt(0)}
          </div>
          <div>
            <h3 className="text-white font-black text-sm uppercase tracking-tight">{d.otelAd}</h3>
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-widest leading-none mt-1">{d.kasaGelirSayisi + d.odemeSayisi} GELİR İŞLEMİ</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-white/60 text-[9px] font-black uppercase tracking-widest mb-1">NET KÂR</p>
          <p className="text-white font-black text-lg tracking-tighter leading-none">{fmt(d.netKar)}</p>
        </div>
      </div>

      <div className="p-8 space-y-7">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-emerald-50/50 border-2 border-emerald-100 rounded-[1.5rem] p-4">
            <p className="text-[9px] text-emerald-600 font-black uppercase tracking-widest mb-1.5 leading-none">TOPLAM GELİR</p>
            <p className="text-base font-black text-emerald-700 tracking-tighter">{fmt(d.toplamGelir)}</p>
          </div>
          <div className="bg-rose-50/50 border-2 border-rose-100 rounded-[1.5rem] p-4">
            <p className="text-[9px] text-rose-500 font-black uppercase tracking-widest mb-1.5 leading-none">TOPLAM GİDER</p>
            <p className="text-base font-black text-rose-700 tracking-tighter">{fmt(d.toplamGider)}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
             <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest mb-2 px-1">
                <span className="text-gray-400">GELİR KIRILIMI</span>
                <span className="text-emerald-500">KASA: %{kasaOrani.toFixed(0)}</span>
             </div>
             <div className="w-full h-2.5 bg-gray-50 rounded-full overflow-hidden flex gap-0.5 p-0.5">
                <div className="h-full bg-emerald-400 rounded-l-full transition-all duration-700" style={{ width: `${kasaOrani}%` }} />
                <div className="h-full bg-indigo-400 rounded-r-full transition-all duration-700" style={{ width: `${rezOrani}%` }} />
             </div>
             <div className="mt-2 flex justify-between px-1">
                <span className="text-[9px] font-bold text-gray-400 uppercase">KASA: {fmt(d.kasaGelirToplam)}</span>
                <span className="text-[9px] font-bold text-gray-400 uppercase">REZ: {fmt(d.rezervasyonOdemeToplam)}</span>
             </div>
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest mb-2.5 px-1">
              <span className="text-gray-400">GİDER / GELİR VERİMLİLİĞİ</span>
              <span className={giderOrani > 80 ? 'text-rose-500' : giderOrani > 50 ? 'text-amber-500' : 'text-emerald-500'}>
                %{giderOrani.toFixed(1)}
              </span>
            </div>
            <div className="w-full h-3 bg-gray-50 rounded-full overflow-hidden border-2 border-white shadow-inner">
               <div
                className={`h-full rounded-full transition-all duration-1000 ${giderOrani > 80 ? 'bg-rose-400' : giderOrani > 50 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                style={{ width: `${giderOrani}%` }}
              />
            </div>
          </div>
        </div>

        <div className={`rounded-2xl px-5 py-4 border-2 flex items-center justify-between ${d.netKar >= 0 ? 'bg-indigo-50/50 border-indigo-100' : 'bg-rose-50/30 border-rose-100'}`}>
          <span className={`text-[10px] font-black uppercase tracking-widest ${d.netKar >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
            {d.netKar >= 0 ? '✓ TİCARİ KÂR OK' : '✗ ZARAR DURUMU'}
          </span>
          <span className={`text-base font-black tracking-tighter ${d.netKar >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>
            %{karMarji} MARJ
          </span>
        </div>

        <div className="bg-violet-50/50 border-2 border-violet-100 rounded-2xl px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black text-violet-500 uppercase tracking-widest">DOLULUK</span>
            <span className="text-base font-black text-violet-700 tracking-tighter">%{doluluk}</span>
          </div>
          <div className="w-full h-2 bg-violet-100 rounded-full overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-violet-400 to-purple-500 rounded-full transition-all duration-700"
              style={{ width: `${doluluk}%` }} />
          </div>
          <p className="text-[9px] font-bold text-violet-400 mt-1.5 uppercase tracking-widest">
            {d.doluOdaSayisi ?? 0} / {d.toplamOdaSayisi ?? 0} oda dolu
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Karşılaştırma çubuk grafiği ────────────────────────────────────────────
function KarsilastirmaGrafigi({ data }) {
  if (data.length === 0) return null;
  const maxGelir = Math.max(...data.map(d => d.toplamGelir), 1);

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 h-full">
      <h3 className="font-black text-gray-900 mb-8 text-xs uppercase tracking-widest">OTEL GELİR KARŞILAŞTIRMASI</h3>
      <div className="space-y-6">
        {[...data].sort((a, b) => b.toplamGelir - a.toplamGelir).map((d, i) => {
          const [from, to] = HOTEL_COLORS[i % HOTEL_COLORS.length];
          const oran = (d.toplamGelir / maxGelir) * 100;
          return (
            <div key={d.otelId} className="group">
              <div className="flex items-center justify-between text-[11px] font-black text-gray-700 uppercase mb-2 px-1 tracking-tight">
                <span>{d.otelAd}</span>
                <span className="text-gray-400 font-bold">{fmt(d.toplamGelir)}</span>
              </div>
              <div className="w-full h-10 bg-gray-50 rounded-2xl overflow-hidden relative border-2 border-white shadow-inner">
                <div
                  className={`h-full bg-gradient-to-r ${from} ${to} rounded-2xl transition-all duration-1000 flex items-center px-4 shadow-lg group-hover:brightness-110`}
                  style={{ width: `${Math.max(oran, 10)}%` }}
                >
                  {oran > 20 && (
                    <span className="text-white text-[9px] font-black uppercase tracking-widest whitespace-nowrap opacity-90">
                      %{oran.toFixed(0)} PAY
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CokOtelOzet() {
  const bugun = bugunStr();
  const ayBasi = ayBasiStr();

  const [aktifTab, setAktifTab] = useState('gunluk');
  const [gunlukData, setGunlukData] = useState([]);
  const [aylikData, setAylikData] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  const [ozelAralik, setOzelAralik] = useState({ bas: ayBasi, bit: bugun });
  const [ozelData, setOzelData] = useState(null);
  const [ozelYukleniyor, setOzelYukleniyor] = useState(false);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    const [gunluk, aylik] = await Promise.all([
      apiRequest(`Otel/cok-otel-ozet?bas=${bugun}&bit=${bugun}`),
      apiRequest(`Otel/cok-otel-ozet?bas=${ayBasi}&bit=${bugun}`)
    ]);
    setGunlukData(gunluk || []);
    setAylikData(aylik || []);
    setYukleniyor(false);
  }, [bugun, ayBasi]);

  useEffect(() => { yukle(); }, [yukle]);

  const ozelAralikSorgu = async () => {
    if (!ozelAralik.bas || !ozelAralik.bit) return;
    setOzelYukleniyor(true);
    const data = await apiRequest(`Otel/cok-otel-ozet?bas=${ozelAralik.bas}&bit=${ozelAralik.bit}`);
    setOzelData(data || []);
    setOzelYukleniyor(false);
  };

  const gosterilen = aktifTab === 'gunluk' ? gunlukData : aktifTab === 'aylik' ? aylikData : (ozelData || []);

  const TABS = [
    { key: 'gunluk', label: 'BUGÜN', sub: new Date().toLocaleDateString('tr-TR') },
    { key: 'aylik', label: 'BU AY', sub: new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' }) },
    { key: 'ozel', label: 'ÖZEL ARALIK', sub: ozelData ? `${ozelAralik.bas} – ${ozelAralik.bit}` : 'ARALIK SEÇİN' },
  ];

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn" style={{ transform: 'translateZ(0)' }}>
      
      {/* Premium Header */}
      <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-sm p-6 md:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-violet-50 rounded-full blur-3xl opacity-60 pointer-events-none" style={{ transform: 'translateZ(0)', willChange: 'transform' }}></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-violet-200">📊</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate uppercase">Çoklu Otel Özeti</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">Global Finansal Performans ve Tesis Karşılaştırmaları</p>
            </div>
          </div>
          <button onClick={yukle} className="bg-white hover:bg-gray-50 text-indigo-600 px-6 py-4 rounded-2xl text-[10px] font-black border-2 border-indigo-50 shadow-sm transition-all uppercase tracking-widest flex items-center gap-2">
            ↺ VERİLERİ YENİLE
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Tab Menüsü */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:w-fit bg-white p-2 rounded-3xl border-2 border-gray-100 shadow-sm">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setAktifTab(t.key)}
              className={`px-8 py-3.5 rounded-[1.25rem] transition-all duration-300 text-center ${
                aktifTab === t.key ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}>
              <p className="text-[11px] font-black uppercase tracking-widest leading-none">{t.label}</p>
              <p className={`text-[9px] font-bold mt-1 opacity-60 uppercase ${aktifTab === t.key ? 'text-indigo-100' : 'text-gray-300'}`}>{t.sub}</p>
            </button>
          ))}
        </div>

        {/* Özel Tarih Aralığı Seçici */}
        {aktifTab === 'ozel' && (
          <div className="bg-white rounded-[2.5rem] border border-indigo-100 shadow-xl shadow-indigo-50 p-8 animate-fadeIn">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 ml-2">Analiz Periyodu Belirleyin</p>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex-1 min-w-[200px] group">
                <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block mb-2.5 ml-4">Başlangıç</label>
                <DatePicker
                  value={isoToDp(ozelAralik.bas)}
                  onChange={v => setOzelAralik(p => ({ ...p, bas: dpToIso(v) }))}
                />
              </div>
              <div className="flex-1 min-w-[200px] group">
                <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block mb-2.5 ml-4">Bitiş</label>
                <DatePicker
                  value={isoToDp(ozelAralik.bit)}
                  onChange={v => setOzelAralik(p => ({ ...p, bit: dpToIso(v) }))}
                />
              </div>
              <button 
                onClick={ozelAralikSorgu} 
                disabled={ozelYukleniyor}
                className="w-full sm:w-auto mt-6 bg-gray-900 text-white text-[11px] font-black px-12 py-5 rounded-2xl shadow-xl hover:bg-indigo-600 transition-all uppercase tracking-widest disabled:opacity-50 h-fit"
              >
                {ozelYukleniyor ? 'ANALİZ YAPILIYOR...' : 'SORGULA VE ANALİZ ET'}
              </button>
            </div>
          </div>
        )}

        {yukleniyor ? (
          <LoadingSpinner fullPage text="Konsolide Rapor Hazırlanıyor..." color="violet" size="lg" />
        ) : (
          <>
            <OzetKartlar data={gosterilen} />

            {gosterilen.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch pt-4">
                <div className="lg:col-span-4 h-full">
                  <KarsilastirmaGrafigi data={gosterilen} />
                </div>
                <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {gosterilen.map((d, i) => (
                    <OtelKart key={d.otelId} d={d} colorIdx={i} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-[2.5rem] border border-gray-100 p-20 text-center">
                 <p className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Bu dönem için finansal veri bulunamadı.</p>
              </div>
            )}
          </>
        )}
      </div>

      <UsageInfo 
        color="violet"
        description="Çoklu Otel Özeti, tüm işletmelerinizin finansal durumunu tek bir ekrandan karşılaştırmalı olarak izlemenizi sağlar. Gelir kırılımları, kâr marjları ve gider oranları ile stratejik kararlar alabilirsiniz."
        scenarios={[
          { title: "Verimlilik Analizi", content: "Otel kartlarındaki 'Gider/Gelir Oranı' %80'in üzerine çıktığında sistem uyarı rengine döner. Bu durum maliyet yönetimine odaklanmanız gerektiğini gösterir." },
          { title: "Kasa vs Rezervasyon", content: "Kasa Geliri elden alınan tahsilatları, Rezervasyon Ödemesi ise banka/kart yoluyla gelen gelirleri temsil eder. Gelir Kırılımı çubuğundan bu dağılımı izleyebilirsiniz." },
          { title: "Karşılaştırma Modu", content: "Sol taraftaki grafik, tüm otellerinizin toplam gelir pastası içindeki paylarını dinamik olarak listeler ve en çok gelir getiren şubeyi belirginleştirir." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
